import { getJson, postJson } from './client';
import { ApiEnvelope } from './migrations';

// FD-410 — the console half of FD-ADR-039's environment reset.
//
// THE CONSOLE IS NOT THE ENFORCEMENT POINT, and every function here is written
// on that assumption. It hides the surface when the capability is off and
// disables the button until the typed name matches, but the SERVER refuses
// independently on all three counts (403 ENVIRONMENT_RESET_DISABLED, 500
// CONFIGURATION_ERROR, 400 CONFIRMATION_MISMATCH). A console-only withhold is
// the FD-296 class of gate — one that only runs where someone happens to be
// standing, and therefore no gate at all against a script, a stale bundle, or a
// second client.
//
// DO NOT "SIMPLIFY" THE SERVER CHECK AWAY on the grounds that the UI already
// prevents it. The UI preventing it is a convenience; the endpoint refusing it
// is the control.

/** `data` payload of GET /v1/environment/reset — the deployment's current state. */
export interface EnvironmentResetState {
  /** Whether this deployment permits the reset at all (ALLOW_ENVIRONMENT_RESET). */
  allowed: boolean;
  /** The name the confirmation is checked against, or null when unconfigured. */
  environmentName: string | null;
  /** How many migrations a reset would remove. */
  migrationCount: number;
  /** What the reset reaches, in the terms the dialog must use. */
  blastRadius: string[];
  /** What it deliberately does not remove — Tenants (D1). */
  retained: string[];
}

/** `data` payload of POST /v1/environment/reset (202). */
export interface EnvironmentResetRun {
  runId: string;
  environmentName: string;
  migrationsFound: number;
  migrationsQueued: number;
  enqueueFailures: string[];
  requestedBy: string;
  requestedUtc: string;
  blastRadius: string[];
  retained: string[];
}

/** One user the FD-409 sweep considered. Reported per user, never a bare count. */
export interface SweepResult {
  tenantId: string;
  displayName: string | null;
  action: 'deleted' | 'left-alone' | 'failed' | 'skipped' | 'already-gone';
  reason: string;
}

/** `data` payload of POST /v1/environment/reset/sweep. */
export interface EnvironmentResetSweep {
  runId: string;
  tenantsSwept: number;
  deleted: number;
  leftAlone: number;
  failed: number;
  skipped: number;
  results: SweepResult[];
}

export async function getEnvironmentResetState(): Promise<EnvironmentResetState> {
  const envelope = await getJson<ApiEnvelope<EnvironmentResetState>>('/v1/environment/reset');
  return envelope.data;
}

export async function requestEnvironmentReset(confirm: string): Promise<EnvironmentResetRun> {
  const envelope = await postJson<ApiEnvelope<EnvironmentResetRun>, { confirm: string }>(
    '/v1/environment/reset',
    { confirm },
  );
  return envelope.data;
}

export async function sweepOrphanedWaveUsers(
  confirm: string,
  runId: string,
): Promise<EnvironmentResetSweep> {
  const envelope = await postJson<ApiEnvelope<EnvironmentResetSweep>, { confirm: string; runId: string }>(
    '/v1/environment/reset/sweep',
    { confirm, runId },
  );
  return envelope.data;
}
